import type { BlockRule, Category, OptionalCategory } from '@cmp/shared';
import { isProtected } from '@cmp/shared';

/**
 * Script and iframe gating.
 *
 * Two modes work together:
 *  1. Manual: authors mark tags `type="text/plain" data-cmp="analytics"`. Deterministic, recommended.
 *  2. Auto: a MutationObserver plus a patched document.createElement neutralise scripts and
 *     iframes whose URL matches a rule before they execute (the pattern popularised by yett).
 *     Requires this script to be the first script in <head>.
 *     Limitation shared with every DOM-level blocker: for tags the HTML parser inserts, the browser
 *     may start the network fetch before we can intervene. Execution is prevented, but an iframe
 *     document can still be requested. Authors should mark parser-inserted embeds with
 *     `data-cmp="marketing" data-cmp-src="https://..."` (no `src`) for a guaranteed no-request.
 *
 * Blocked nodes are kept so they can be re-injected once the matching category is granted.
 */

export const BLOCKED_TYPE = 'javascript/blocked';
const MANUAL_TYPE = 'text/plain';
const ATTR = 'data-cmp';

interface BlockedScript {
  kind: 'script';
  category: OptionalCategory;
  node: HTMLScriptElement;
  /** True when the author gated it explicitly (text/plain + data-cmp). */
  manual: boolean;
}
interface BlockedFrame {
  kind: 'iframe';
  category: OptionalCategory;
  node: HTMLIFrameElement;
  src: string;
  manual: boolean;
}
type Blocked = BlockedScript | BlockedFrame;

export type PlaceholderRenderer = (frame: HTMLIFrameElement, category: OptionalCategory) => void;

export class Blocker {
  /** Only one blocker may be live per document; a second start() supersedes the first. */
  private static active: Blocker | null = null;
  private rules: BlockRule[] = [];
  private blocked: Blocked[] = [];
  private granted = new Set<Category>(['necessary']);
  private observer: MutationObserver | null = null;
  private started = false;
  private placeholder: PlaceholderRenderer | null = null;

  constructor(rules: BlockRule[], placeholder?: PlaceholderRenderer) {
    this.rules = rules.map((r) => ({ match: r.match.toLowerCase(), category: r.category }));
    this.placeholder = placeholder ?? null;
  }

  addRules(rules: BlockRule[]): void {
    for (const r of rules) this.rules.push({ match: r.match.toLowerCase(), category: r.category });
  }

  setPlaceholder(renderer: PlaceholderRenderer): void {
    this.placeholder = renderer;
    for (const b of this.blocked) if (b.kind === 'iframe') renderer(b.node, b.category);
  }

  /** Manual mode: drop URL rules and let anything auto-detected so far run. Manual tags stay gated. */
  disableAutoBlock(): void {
    this.rules = [];
    const keep: Blocked[] = [];
    for (const b of this.blocked) {
      if (b.manual) keep.push(b);
      else this.activate(b);
    }
    this.blocked = keep;
  }

  /** Category a URL belongs to, or null if it is not gated. */
  categoryFor(url: string | null | undefined): OptionalCategory | null {
    if (!url) return null;
    // Payments, checkout, fraud checks, captchas and sign-in always run. No rule can hold them.
    if (isProtected(url)) return null;
    const u = url.toLowerCase();
    for (const r of this.rules) if (u.indexOf(r.match) !== -1) return r.category;
    return null;
  }

  isGranted(category: Category): boolean {
    return this.granted.has(category);
  }

  /** Begin intercepting. Safe to call once; later calls are ignored. */
  start(): void {
    if (this.started) return;
    this.started = true;
    Blocker.active?.stop();
    Blocker.active = this;
    this.patchCreateElement();
    this.observer = new MutationObserver((muts) => {
      for (const m of muts) {
        for (let i = 0; i < m.addedNodes.length; i++) this.inspect(m.addedNodes[i]!);
      }
    });
    this.observer.observe(document.documentElement, { childList: true, subtree: true });
    // Anything already in the DOM (e.g. manual text/plain tags in <head> before us).
    this.sweep();
  }

  /** Stop observing. The createElement patch stays installed but becomes a no-op for this instance. */
  stop(): void {
    this.observer?.disconnect();
    this.observer = null;
    if (Blocker.active === this) Blocker.active = null;
  }

  private isActive(): boolean {
    return Blocker.active === this;
  }

  /** Release everything whose category is now allowed. Idempotent. */
  release(granted: Category[]): void {
    this.granted = new Set<Category>(['necessary', ...granted]);
    const keep: Blocked[] = [];
    for (const b of this.blocked) {
      if (this.granted.has(b.category)) this.activate(b);
      else keep.push(b);
    }
    this.blocked = keep;
  }

  /** Re-scan the document for manual tags, useful for SPAs after route changes. */
  sweep(): void {
    const scripts = document.querySelectorAll<HTMLScriptElement>(`script[type="${MANUAL_TYPE}"][${ATTR}]`);
    scripts.forEach((s) => this.inspect(s));
    const frames = document.querySelectorAll<HTMLIFrameElement>(`iframe[${ATTR}-src]`);
    frames.forEach((f) => this.inspect(f));
  }

  private inspect(node: Node): void {
    if (node.nodeType !== 1) return;
    const el = node as HTMLElement;
    if (el.tagName === 'SCRIPT') this.inspectScript(el as HTMLScriptElement);
    else if (el.tagName === 'IFRAME') this.inspectFrame(el as HTMLIFrameElement);
  }

  private inspectScript(s: HTMLScriptElement): void {
    if (s.getAttribute(ATTR) === 'ignore') return;
    const manual = s.getAttribute(ATTR) as OptionalCategory | null;
    const typeAttr = s.getAttribute('type');
    let category: OptionalCategory | null = null;

    const isManual = !!manual && typeAttr === MANUAL_TYPE;
    if (isManual) {
      category = manual;
    } else if (typeAttr !== MANUAL_TYPE) {
      // Plain script, or one already neutralised by the createElement patch.
      category = this.categoryFor(s.src || s.getAttribute('src'));
    }
    if (!category) return;
    if (this.granted.has(category)) {
      if (typeAttr === MANUAL_TYPE || typeAttr === BLOCKED_TYPE) this.activate({ kind: 'script', category, node: s, manual: isManual });
      return;
    }
    if (this.blocked.some((b) => b.node === s)) return;

    if (!isManual) {
      s.type = BLOCKED_TYPE;
      // Firefox only: cancels execution of a parser-inserted script.
      s.addEventListener('beforescriptexecute', (e) => {
        if (s.getAttribute('type') === BLOCKED_TYPE) e.preventDefault();
      });
      // Removing the node stops Chromium/Safari from executing a parser-inserted external script.
      s.parentNode?.removeChild(s);
    }
    // Manual text/plain tags are inert already; leave them in place so the DOM stays inspectable.
    this.blocked.push({ kind: 'script', category, node: s, manual: isManual });
  }

  private inspectFrame(f: HTMLIFrameElement): void {
    if (f.getAttribute(ATTR) === 'ignore') return;
    const stored = f.getAttribute(`${ATTR}-src`);
    const src = stored ?? f.getAttribute('src');
    const manualCat = f.getAttribute(ATTR) as OptionalCategory | null;
    const category = manualCat ?? this.categoryFor(src);
    if (!category || !src) return;
    const manual = !!manualCat;
    if (this.granted.has(category)) {
      if (stored) this.activate({ kind: 'iframe', category, node: f, src, manual });
      return;
    }
    if (this.blocked.some((b) => b.node === f)) return;
    f.setAttribute(`${ATTR}-src`, src);
    f.removeAttribute('src');
    f.src = 'about:blank';
    this.blocked.push({ kind: 'iframe', category, node: f, src, manual });
    this.placeholder?.(f, category);
  }

  private activate(b: Blocked): void {
    if (b.kind === 'iframe') {
      b.node.removeAttribute(`${ATTR}-src`);
      b.node.removeAttribute('srcdoc');
      b.node.src = b.src;
      return;
    }
    const old = b.node;
    const fresh = document.createElement('script');
    fresh.setAttribute(ATTR, 'ignore');
    for (const a of Array.from(old.attributes)) {
      if (a.name === 'type' || a.name === ATTR) continue;
      fresh.setAttribute(a.name, a.value);
    }
    if (old.src) fresh.src = old.src;
    else fresh.text = old.text;
    // Re-insert where the original was if it is still attached, else append to head.
    const parent = old.parentNode;
    if (parent) parent.replaceChild(fresh, old);
    else (document.head || document.documentElement).appendChild(fresh);
  }

  /** Neutralise dynamically created scripts and iframes before a src is even assigned. */
  private patchCreateElement(): void {
    const create = document.createElement;
    const srcDesc = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'src');
    const typeDesc = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'type');
    const frameSrcDesc = Object.getOwnPropertyDescriptor(HTMLIFrameElement.prototype, 'src');
    if (!srcDesc?.set || !typeDesc?.set || !typeDesc.get) return;
    const self = this;
    document.createElement = function (this: Document, tag: string, options?: ElementCreationOptions) {
      const el = create.call(this, tag, options);
      if (typeof tag !== 'string') return el;
      const name = tag.toLowerCase();
      if (name === 'iframe' && frameSrcDesc?.set) {
        const frame = el as HTMLIFrameElement;
        try {
          Object.defineProperty(frame, 'src', {
            configurable: true,
            enumerable: true,
            get() {
              return frameSrcDesc.get!.call(this);
            },
            set(value: string) {
              const cat = self.isActive() ? self.categoryFor(value) : null;
              if (cat && !self.granted.has(cat) && frame.getAttribute(ATTR) !== 'ignore') {
                // Park the real URL; the observer will register the frame and draw the placeholder.
                frame.setAttribute(`${ATTR}-src`, value);
                frameSrcDesc.set!.call(this, 'about:blank');
                return;
              }
              frameSrcDesc.set!.call(this, value);
            },
          });
          const setAttr = frame.setAttribute.bind(frame);
          frame.setAttribute = (n: string, v: string) => (n === 'src' ? void (frame.src = v) : setAttr(n, v));
        } catch {
          /* fall back to observer-only */
        }
        return el;
      }
      if (name !== 'script') return el;
      const script = el as HTMLScriptElement;
      try {
        Object.defineProperty(script, 'src', {
          configurable: true,
          enumerable: true,
          get() {
            return srcDesc.get!.call(this);
          },
          set(value: string) {
            const cat = self.isActive() ? self.categoryFor(value) : null;
            if (cat && !self.granted.has(cat) && script.getAttribute(ATTR) !== 'ignore') {
              typeDesc.set!.call(this, BLOCKED_TYPE);
            }
            srcDesc.set!.call(this, value);
          },
        });
        const setAttribute = script.setAttribute.bind(script);
        script.setAttribute = (name: string, value: string) => {
          if (name === 'src') script.src = value;
          else if (name === 'type' && script.getAttribute('type') === BLOCKED_TYPE) return;
          else setAttribute(name, value);
        };
      } catch {
        /* another script patched createElement; fall back to observer-only */
      }
      return el;
    } as typeof document.createElement;
  }
}
